import {
  Copy,
  Download,
  Eye,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { TopBar } from '../components/TopBar'
import { useI18n } from '../lib/i18n'
import {
  fetchAllDocuments,
  deleteMatterDocument,
  type MatterDocument,
} from '../lib/api'
import { exportDocToDocx } from '../lib/exportChat'

// ─── i18n ────────────────────────────────────────────────────────────────────
const LABELS = {
  zh: {
    pageTitle: '文书模板',
    subtitle: '管理和查看所有生成的法律文书草稿',
    searchPlaceholder: '搜索文书...',
    filterAll: '全部',
    filterCase: '案件分析类',
    filterCourt: '法庭文书类',
    filterInternal: '内部文档',
    totalDocs: '总文书数',
    caseAnalysis: '案件分析类',
    courtDocs: '法庭文书类',
    internalDocs: '内部工作文档',
    sectionCase: '案件分析类',
    sectionCourt: '法庭文书类',
    sectionInternal: '内部文档',
    noDocs: '暂无文书，请在案件详情页生成',
    loading: '加载中...',
    viewDoc: '查看全文',
    closeDoc: '关闭',
    copySuccess: '已复制',
    copyFailed: '复制失败',
    exportWord: '导出Word',
    deleteDoc: '删除',
    deleteConfirm: '确定要删除这份文书吗？',
    matterLabel: '所属案件',
    createdAtLabel: '生成于',
  },
  en: {
    pageTitle: 'Document Templates',
    subtitle: 'Manage and view all generated legal document drafts',
    searchPlaceholder: 'Search documents...',
    filterAll: 'All',
    filterCase: 'Case Analysis',
    filterCourt: 'Court Documents',
    filterInternal: 'Internal',
    totalDocs: 'Total Documents',
    caseAnalysis: 'Case Analysis',
    courtDocs: 'Court Documents',
    internalDocs: 'Internal Documents',
    sectionCase: 'Case Analysis',
    sectionCourt: 'Court Documents',
    sectionInternal: 'Internal',
    noDocs: 'No documents yet. Generate from a matter detail page.',
    loading: 'Loading...',
    viewDoc: 'View Full Document',
    closeDoc: 'Close',
    copySuccess: 'Copied',
    copyFailed: 'Copy failed',
    exportWord: 'Export Word',
    deleteDoc: 'Delete',
    deleteConfirm: 'Are you sure you want to delete this document?',
    matterLabel: 'Matter',
    createdAtLabel: 'Created',
  },
}

type FilterTab = 'all' | 'case' | 'court' | 'internal'

const DOC_TYPE_CATEGORY: Record<string, 'case' | 'court' | 'internal'> = {
  case_summary: 'case',
  witness_statement: 'court',
  legal_opinion: 'case',
  statement_of_claim: 'court',
  statement_of_defense: 'court',
  settlement_agreement: 'court',
  internal_memo: 'internal',
}

export function Templates() {
  const { lang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh

  const [filter, setFilter] = useState<FilterTab>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [documents, setDocuments] = useState<MatterDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [viewDoc, setViewDoc] = useState<MatterDocument | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<MatterDocument | null>(null)

  const loadDocs = useCallback(async () => {
    setLoading(true)
    try {
      const docs = await fetchAllDocuments()
      setDocuments(docs)
    } catch {
      /* best-effort */
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadDocs()
  }, [loadDocs])

  const getCategory = (doc: MatterDocument): 'case' | 'court' | 'internal' =>
    DOC_TYPE_CATEGORY[doc.doc_type] ?? 'case'

  const filtered = documents.filter((doc) => {
    if (filter !== 'all' && getCategory(doc) !== filter) return false
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      return (
        doc.title.toLowerCase().includes(q) ||
        (doc.matter_title ?? '').toLowerCase().includes(q) ||
        (doc.content ?? '').toLowerCase().includes(q)
      )
    }
    return true
  })

  const caseDocs = filtered.filter((d) => getCategory(d) === 'case')
  const courtDocs = filtered.filter((d) => getCategory(d) === 'court')
  const internalDocs = filtered.filter((d) => getCategory(d) === 'internal')

  const stats = {
    total: documents.length,
    case: documents.filter((d) => getCategory(d) === 'case').length,
    court: documents.filter((d) => getCategory(d) === 'court').length,
    internal: documents.filter((d) => getCategory(d) === 'internal').length,
  }

  const handleCopy = async (doc: MatterDocument) => {
    if (!doc.content) return
    try {
      await navigator.clipboard.writeText(doc.content)
      setCopiedId(doc.doc_id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch { /* ignore */ }
  }

  const handleExport = (doc: MatterDocument) => {
    if (!doc.content) return
    exportDocToDocx({ title: doc.title, content: doc.content, lang })
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMatterDocument(deleteTarget.matter_id, deleteTarget.doc_id)
      setDeleteTarget(null)
      await loadDocs()
    } catch { /* ignore */ }
  }

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    } catch {
      return iso
    }
  }

  return (
    <>
      <TopBar title={t.pageTitle} />
      <div className="flex-1 overflow-y-auto p-6">
        {/* 标题行 */}
        <div className="mb-5">
          <h2 className="text-xl font-bold text-brand-800">{t.pageTitle}</h2>
          <p className="text-sm text-brand-400 mt-0.5">{t.subtitle}</p>
        </div>

        {/* 搜索 + 筛选 */}
        <div className="flex items-center gap-4 mb-5">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
            />
          </div>

          <div className="flex items-center bg-brand-100 rounded-lg p-0.5 ml-auto">
            {(
              [
                ['all', t.filterAll],
                ['case', t.filterCase],
                ['court', t.filterCourt],
                ['internal', t.filterInternal],
              ] as [FilterTab, string][]
            ).map(([tab, label]) => {
              const isActive = filter === tab
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setFilter(tab)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-brand-600 hover:text-brand-800'
                  }`}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatBox value={stats.total} label={t.totalDocs} color="bg-blue-600" />
          <StatBox value={stats.case} label={t.caseAnalysis} color="bg-purple-600" />
          <StatBox value={stats.court} label={t.courtDocs} color="bg-green-600" />
          <StatBox value={stats.internal} label={t.internalDocs} color="bg-amber-600" />
        </div>

        {/* 文书列表 */}
        {loading ? (
          <div className="text-center py-12 text-sm text-brand-400">{t.loading}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-sm text-brand-400">{t.noDocs}</div>
        ) : (
          <>
            {caseDocs.length > 0 && (
              <DocSection title={t.sectionCase} color="bg-blue-500" docs={caseDocs} t={t}
                onCopy={handleCopy} onExport={handleExport} onDelete={setDeleteTarget}
                onView={setViewDoc} formatDate={formatDate} />
            )}
            {courtDocs.length > 0 && (
              <DocSection title={t.sectionCourt} color="bg-green-500" docs={courtDocs} t={t}
                onCopy={handleCopy} onExport={handleExport} onDelete={setDeleteTarget}
                onView={setViewDoc} formatDate={formatDate} />
            )}
            {internalDocs.length > 0 && (
              <DocSection title={t.sectionInternal} color="bg-amber-500" docs={internalDocs} t={t}
                onCopy={handleCopy} onExport={handleExport} onDelete={setDeleteTarget}
                onView={setViewDoc} formatDate={formatDate} />
            )}
          </>
        )}

        {/* 全文查看弹窗 */}
        {viewDoc && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setViewDoc(null)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col mx-4">
              <div className="p-5 border-b border-brand-100 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-brand-800">{viewDoc.title}</h3>
                  {viewDoc.matter_title && (
                    <p className="text-xs text-brand-400 mt-0.5">{t.matterLabel}: {viewDoc.matter_title}</p>
                  )}
                </div>
                <button type="button" onClick={() => setViewDoc(null)} className="p-1 rounded text-brand-400 hover:bg-brand-100">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <div className="prose prose-sm max-w-none text-brand-700 whitespace-pre-wrap leading-relaxed">
                  {viewDoc.content}
                </div>
              </div>
              <div className="p-4 border-t border-brand-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => handleCopy(viewDoc)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-brand-600 hover:bg-brand-100 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {copiedId === viewDoc.doc_id ? t.copySuccess : t.copyFailed}
                </button>
                <button
                  type="button"
                  onClick={() => handleExport(viewDoc)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-brand-600 hover:bg-brand-100 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  {t.exportWord}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 删除确认弹窗 */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4">
              <div className="p-5">
                <h3 className="text-base font-bold text-brand-800 mb-2">{t.deleteDoc}</h3>
                <p className="text-sm text-brand-600 mb-4">{t.deleteConfirm}</p>
                <p className="text-xs text-brand-400 bg-brand-50 rounded-lg p-2 mb-4">{deleteTarget.title}</p>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(null)}
                    className="px-4 py-2 rounded-lg text-sm text-brand-600 hover:bg-brand-100 transition-colors"
                  >
                    {t.closeDoc}
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                  >
                    {t.deleteDoc}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// ─── 统计盒子 ────────────────────────────────────────────────────────────────
function StatBox({
  value,
  label,
  color,
}: {
  value: number
  label: string
  color: string
}) {
  return (
    <div className={`rounded-xl p-4 text-white ${color}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-white/80 mt-0.5">{label}</div>
    </div>
  )
}

// ─── 文书分类区块 ────────────────────────────────────────────────────────────
type DocCardProps = {
  t: (typeof LABELS)['zh']
  lang: 'zh' | 'en'
  onCopy: (doc: MatterDocument) => void
  onExport: (doc: MatterDocument) => void
  onDelete: (doc: MatterDocument) => void
  onView: (doc: MatterDocument) => void
  copiedId: string | null
  formatDate: (iso: string) => string
}

function DocSection({
  title,
  color,
  docs,
  t,
  onCopy,
  onExport,
  onDelete,
  onView,
  formatDate,
}: Omit<DocCardProps, 'lang' | 'copiedId'> & { title: string; color: string; docs: MatterDocument[] }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
        <h4 className="text-sm font-semibold text-brand-800">{title}</h4>
      </div>
      <div className="space-y-3">
        {docs.map((doc) => (
          <DocCard
            key={doc.doc_id}
            doc={doc}
            t={t}
            onCopy={onCopy}
            onExport={onExport}
            onDelete={onDelete}
            onView={onView}
            formatDate={formatDate}
          />
        ))}
      </div>
    </div>
  )
}

// ─── 文书卡片 ────────────────────────────────────────────────────────────────
function DocCard({
  doc,
  t,
  onCopy,
  onExport,
  onDelete,
  onView,
  formatDate,
}: { doc: MatterDocument } & Omit<DocCardProps, 'copiedId' | 'lang'>) {
  return (
    <div className="bg-white rounded-xl border border-brand-200 p-5 hover:border-brand-300 transition-colors">
      {/* 标题行 */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h5 className="text-sm font-semibold text-brand-800">{doc.title}</h5>
          {doc.matter_title && (
            <span className="text-xs text-brand-400">{t.matterLabel}: {doc.matter_title}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => onCopy(doc)}
          className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
          title="Copy"
        >
          <Copy className="w-4 h-4" />
        </button>
      </div>

      {/* 预览文本 */}
      <div className="text-xs text-brand-500 leading-relaxed bg-brand-50 rounded-lg p-3 mb-3 line-clamp-3 font-mono">
        {doc.content || ''}
      </div>

      {/* 底部 */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-brand-400 flex items-center gap-1">
          {formatDate(doc.created_at)}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onView(doc)}
            className="p-1.5 rounded text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
            title={t.viewDoc}
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onExport(doc)}
            className="p-1.5 rounded text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
            title={t.exportWord}
          >
            <Download className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(doc)}
            className="p-1.5 rounded text-brand-400 hover:bg-red-50 hover:text-red-600 transition-colors"
            title={t.deleteDoc}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}
